import { Address } from "../models/utilities/address";
import { AppUtils } from "../utils/app-utils";
import { toLocalDateString } from "../utils/date-utils";
import { GlobalConstants } from "../utils/app-constants";
import { OrderProductionStatus } from "../models/ordering/enums/order-production-status";
import { DeliveryStatus } from "./models/enums/delivery-status";
import { ShipmentActionType } from "./models/enums/shipment-action-type";
import { ShipmentStatus } from "./models/enums/shipment-status";
import { BoxTruckRead, TractorRead, TrailerRead } from "./models/fleet/asset-read";
import { OrderSummary } from "./models/shared/order-summary";
import { RunningTotal } from "./models/shared/running-total";
import { DeliveryShipmentAction, EndShipmentAction, ShipmentAction, StartShipmentAction, TravelShipmentAction } from "./models/shipments/shipment-action";
import { ShipmentRead } from "./models/shipments/shipment-read";
import { TruckRead } from "./models/trucks/truck-read";

/**
 * A collection of transportation helper functions
 */
export abstract class TransportationUtils {

    /**
     * Check if a shipment of the given service center has to be produced.  Those that do go from open to
     * in production to ready to ship, and the others from open to ready to ship.
     * @param serviceCenterId The service center of the shipment
     */
    public static requiresProduction(serviceCenterId : string) : boolean {
        return serviceCenterId === GlobalConstants.MainServiceCenter
            || serviceCenterId === GlobalConstants.TerreHauteServiceCenter;
    }

    /**
     * The status a shipment moves to next: open to in production when the shipment has to be produced, and otherwise
     * to ready to ship, then to in-transit and finally closed.  A closed or cancelled shipment has none.
     * @param status The status of the shipment
     * @param serviceCenterId The service center of the shipment
     */
    public static getNextStatus(status : ShipmentStatus, serviceCenterId : string) : ShipmentStatus | undefined {
        switch (status) {
            case ShipmentStatus.Open:
                return TransportationUtils.requiresProduction(serviceCenterId)
                    ? ShipmentStatus.InProduction
                    : ShipmentStatus.ReadyToShip;
            case ShipmentStatus.InProduction:
                return ShipmentStatus.ReadyToShip;
            case ShipmentStatus.ReadyToShip:
                return ShipmentStatus.InTransit;
            case ShipmentStatus.InTransit:
                return ShipmentStatus.Closed;
            default:
                return undefined;
        }
    }

    /**
     * The status that a selection of shipments are all moved to together.  There is one only when every shipment is
     * of the same status and has the same one next, which they do not when open shipments of a service center that
     * produces are among those that do not.
     * @param shipments The shipments that were selected
     */
    public static getSharedNextStatus(shipments : Array<{ status : ShipmentStatus, serviceCenterId : string }>) : ShipmentStatus | undefined {

        if (shipments.length === 0 || shipments.some(x => x.status !== shipments[0].status)) {
            return undefined;
        }

        const next = TransportationUtils.getNextStatus(shipments[0].status, shipments[0].serviceCenterId);
        return shipments.every(x => TransportationUtils.getNextStatus(x.status, x.serviceCenterId) === next)
            ? next
            : undefined;
    }

    /**
     * Check if the provided trucks has any stops that have
     * been delivered
     * @param truck A truck object to check against
     */
    public static hasDeliveredStop(truck : ShipmentRead) {
        return truck.actions.filter((stop : ShipmentAction) => {
            return stop.type === ShipmentActionType.Delivery;
        }).some((itm : DeliveryShipmentAction) => {
            return itm.status !== DeliveryStatus.Open;
        });
    }

    /**
     * The orders on the route that still have to be produced.  A route is only ready to ship once every
     * order on it is produced, or does not need producing.
     * @param route A route object to check against
     */
    public static getUnproducedOrders(route : ShipmentRead) : OrderSummary[] {
        return TransportationUtils.findUnproducedOrders(
            route.actions
                .filter((stop : ShipmentAction) => stop.type === ShipmentActionType.Delivery)
                .flatMap((stop : DeliveryShipmentAction) => stop.orders)
        );
    }

    /**
     * The orders, of the ones given, that still have to be produced.  An order is ready once it is
     * produced, or when it does not need producing.
     * @param orders The orders on a route, a stop or a pickup
     */
    public static findUnproducedOrders(orders : OrderSummary[]) : OrderSummary[] {
        return (orders || []).filter((order : OrderSummary) =>
            order.productionStatus !== OrderProductionStatus.NotApplicable &&
            order.productionStatus !== OrderProductionStatus.Produced
        );
    }

    /**
     * Check if the provided route has any stops that have 
     * been delivered
     * @param route A route object to check against
     */
    public static hasOpenDeliveries(route : ShipmentRead) {
        return route.actions.filter((stop : ShipmentAction) => {
            return stop.type === ShipmentActionType.Delivery;
        }).some((itm : DeliveryShipmentAction) => {
            return itm.status === DeliveryStatus.Open;
        });
    }

    /***
     * Check if the provided address is valid or not
     * @returns true if the address has all of lat/long/country/timzeon
     */
    public static isValidAddress(addr : Address) {
        if (addr &&
            addr.latitude && 
            addr.longitude &&
            addr.country && addr.country.length > 0 && 
            addr.timeZoneId && addr.timeZoneId.length > 0) {
              return true;
        }
        else {
            return false;
        }
    }

   /**
    * Rollup and return a list of metrics based on the
    * truck and orders on this stop
    * @param truckTotal The totals for all the other stops on the truck
    * @param onStop A list of orders that are on the stop currently
    * @param newOrders A list of new orders to sum with the onStop orders
    */
    public static calculateTruckTotals = (truckTotal : RunningTotal|undefined, onStop : OrderSummary[], newOrders : OrderSummary[]) => {

    //    if (AppUtils.IsEmpty(truckTotal)) {
    //        return;
    //    }

       const newTotals = new RunningTotal();

       // Our initial order counts for the rest of the truck
       if (truckTotal) {
           newTotals.units     = truckTotal.units;
           newTotals.weight    = truckTotal.weight;
           newTotals.singles   = truckTotal.singles;
           newTotals.sections  = truckTotal.sections;
           newTotals.track     = truckTotal.track;
           newTotals.hardware  = truckTotal.hardware;
       }

       // Add orders on stop
       onStop.forEach((itm : OrderSummary) => {
           newTotals.units     += itm.units;
           newTotals.weight    += itm.weight;
           newTotals.singles   += itm.singles;
           newTotals.sections  += itm.sections;
           newTotals.track     += itm.track;
           newTotals.hardware  += itm.hardware;
       });

       // Add new order count
       newOrders.forEach((itm : OrderSummary) => {
           newTotals.units     += itm.units;
           newTotals.weight    += itm.weight;
           newTotals.singles   += itm.singles;
           newTotals.sections  += itm.sections;
           newTotals.track     += itm.track;
           newTotals.hardware  += itm.hardware;
       });

       return newTotals;
   }

   /**
    * Rollup and return a list of metrics based on the
    * truck and orders on this stop
    * @param truckTotal The totals for all the other stops on the truck
    * @param onStop A list of orders that are on the stop currently
    * @param newOrders A list of new orders to sum with the onStop orders
    */
    public static subtractTruckTotals = (truckTotal : RunningTotal|undefined, onStop : OrderSummary[], newOrders : OrderSummary[]) => {

    //    if (AppUtils.IsEmpty(truckTotal)) {
    //        return;
    //    }

       const newTotals = new RunningTotal();

       // Our initial order counts for the rest of the truck
       if (truckTotal) {
           newTotals.units     = truckTotal.units;
           newTotals.weight    = truckTotal.weight;
           newTotals.singles   = truckTotal.singles;
           newTotals.sections  = truckTotal.sections;
           newTotals.track     = truckTotal.track;
           newTotals.hardware  = truckTotal.hardware;
       }

       // Add orders on stop
       onStop.forEach((itm : OrderSummary) => {
           newTotals.units     += itm.units;
           newTotals.weight    += itm.weight;
           newTotals.singles   += itm.singles;
           newTotals.sections  += itm.sections;
           newTotals.track     += itm.track;
           newTotals.hardware  += itm.hardware;
       });

       // Add new order count
       newOrders.forEach((itm : OrderSummary) => {
           newTotals.units     -= itm.units;
           newTotals.weight    -= itm.weight;
           newTotals.singles   -= itm.singles;
           newTotals.sections  -= itm.sections;
           newTotals.track     -= itm.track;
           newTotals.hardware  -= itm.hardware;
       });

       return newTotals;
   }


    public static buildComputedProperties(tmpTruck : TruckRead) {
            
        const tmpTruckTotal = new RunningTotal();
        let totalTravel = '00:00:00';
        let totalDisance = 0;

        tmpTruck.actions.forEach((itm : ShipmentAction) => {

            // Figure out start time
            if (itm.type === ShipmentActionType.Origin) {
                const departTime = (itm as StartShipmentAction);
                tmpTruck.departureTime = departTime.actionTime ? toLocalDateString(departTime.actionTime.local, departTime.actionTime.timeZone) : '';
            }
            // Figure out the end time
            else if (itm.type === ShipmentActionType.Destination) {
                const departTime = (itm as EndShipmentAction);
                tmpTruck.returnTime = departTime.actionTime ? toLocalDateString(departTime.actionTime.local, departTime.actionTime.timeZone) : '';
            }
            // Add metrics to running total
            else if (itm.type === ShipmentActionType.Delivery) {
                const cdStop = (itm as DeliveryShipmentAction);
                cdStop.orders.forEach((ordItm : OrderSummary) => {
                    tmpTruckTotal.units      += ordItm.units;
                    tmpTruckTotal.weight     += ordItm.weight;
                    tmpTruckTotal.singles    += ordItm.singles;
                    tmpTruckTotal.sections   += ordItm.sections;
                    tmpTruckTotal.track      += ordItm.track;
                    tmpTruckTotal.hardware   += ordItm.hardware;
                    tmpTruckTotal.feet       += ordItm.feet;
                });
            }

            if (itm.type !== ShipmentActionType.Origin) {
                totalTravel = AppUtils.addTimeSpansFromServer(totalTravel, (itm as TravelShipmentAction).travelTime);
                totalDisance += (itm as TravelShipmentAction).distance;
            }
        });

        tmpTruck.metrics = tmpTruckTotal;
        tmpTruck.totalDistance = totalDisance;
        tmpTruck.totalTravelTime = totalTravel;
        
        return tmpTruck;
    }

    public static computeTotalTravelTime = (stops : ShipmentAction[]) => {

        let totalTravel = '00:00:00';
        stops.forEach((itm : DeliveryShipmentAction) => {
            if (itm.type !== ShipmentActionType.Origin) {
                totalTravel = AppUtils.addTimeSpansFromServer(totalTravel, itm.travelTime);
            }
        });

        return totalTravel;
    }

    public static computeTotalDistance = (stops: ShipmentAction[]) => {

        const distance = stops.reduce((acc : number, itm : ShipmentAction) => {
            if (itm.type !== ShipmentActionType.Origin) {
                return acc + (itm as TravelShipmentAction).distance;
            }
            return acc;
        }, 0);
        
        return distance;
    }

    public static AssetTypeMapper = (type : any) => {
        switch (type) {
            case 0: 
                return TractorRead;
            case 1:
                return TrailerRead;
            case 2:
                return BoxTruckRead;
            default:
                return {};
        }
    }
}
